import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { APIProvider, Map, AdvancedMarker, useMapsLibrary } from "@vis.gl/react-google-maps";
import { MapPin, Navigation, Search, Link2, X, ExternalLink, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAP_ID = "hk-delivery-map";

/** Fetch the public Google Maps integration config once per page. */
export const useGoogleMapsConfig = () => {
  const [config, setConfig] = useState(null);
  useEffect(() => {
    axios.get(`${API}/settings/google_maps`)
      .then((r) => setConfig(r.data))
      .catch(() => setConfig({ enabled: false }));
  }, []);
  return config;
};

const mapsUrlFor = (v) =>
  v?.google_maps_url ||
  (v?.latitude != null && v?.longitude != null
    ? `https://www.google.com/maps?q=${v.latitude},${v.longitude}`
    : null);

const SOURCE_LABELS = {
  current_location: "Current location",
  map_pin: "Map pin",
  google_search: "Google search",
  pasted_link: "Pasted link",
};

/** Parse lat/lng out of a Google Maps link; returns {lat, lng} or null. */
const parseMapsLink = (raw) => {
  const url = (raw || "").trim();
  if (!/(google\.[a-z.]+\/maps|maps\.app\.goo\.gl|goo\.gl\/maps)/i.test(url)) return { valid: false };
  let m = url.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
  if (m) return { valid: true, lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
  m = url.match(/[?&](?:q|query|ll)=(-?\d+\.?\d*)[,+%20]+(-?\d+\.?\d*)/);
  if (m) return { valid: true, lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
  return { valid: true, lat: null, lng: null }; // short link — saved as-is
};

// ---------- Inner panels (need Maps libraries, so rendered inside APIProvider) ----------

const useGeocoder = () => {
  const geocoding = useMapsLibrary("geocoding");
  const ref = useRef(null);
  useEffect(() => {
    if (geocoding && !ref.current) ref.current = new geocoding.Geocoder();
  }, [geocoding]);
  return useCallback(async (lat, lng) => {
    if (!ref.current) return "";
    try {
      const r = await ref.current.geocode({ location: { lat, lng } });
      return r?.results?.[0]?.formatted_address || "";
    } catch {
      return "";
    }
  }, []);
};

const PinMap = ({ value, onPick, testid }) => {
  const reverseGeocode = useGeocoder();
  const center = value?.latitude != null
    ? { lat: value.latitude, lng: value.longitude }
    : { lat: 20.5937, lng: 78.9629 }; // India centroid
  const pick = async (lat, lng) => {
    const formatted = await reverseGeocode(lat, lng);
    onPick({
      latitude: lat, longitude: lng,
      formatted_address: formatted,
      google_maps_url: `https://www.google.com/maps?q=${lat},${lng}`,
      location_source: "map_pin",
    });
  };
  return (
    <div className="rounded-md overflow-hidden border border-[var(--hk-border)]" data-testid={`${testid}-map`}>
      <Map
        style={{ width: "100%", height: 260 }}
        defaultCenter={center}
        defaultZoom={value?.latitude != null ? 16 : 4.5}
        mapId={MAP_ID}
        gestureHandling="greedy"
        disableDefaultUI
        zoomControl
        onClick={(e) => pick(e.detail.latLng.lat, e.detail.latLng.lng)}
      >
        {value?.latitude != null && (
          <AdvancedMarker
            position={{ lat: value.latitude, lng: value.longitude }}
            draggable
            onDragEnd={(e) => pick(e.latLng.lat(), e.latLng.lng())}
          />
        )}
      </Map>
      <p className="text-[11px] text-[var(--hk-ink-3)] px-3 py-2 bg-[var(--hk-warm-ivory)]">Tap the map or drag the pin to the exact delivery spot.</p>
    </div>
  );
};

const SearchBox = ({ onPick, onAddressParts, testid }) => {
  const places = useMapsLibrary("places");
  const reverseGeocode = useGeocoder();
  const inputRef = useRef(null);
  const acRef = useRef(null);
  useEffect(() => {
    if (!places || !inputRef.current || acRef.current) return;
    try {
      const ac = new places.Autocomplete(inputRef.current, {
        fields: ["geometry", "formatted_address", "address_components"],
        types: ["address"],
        componentRestrictions: { country: "in" },
      });
      ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        const loc = place?.geometry?.location;
        if (!loc) return;
        const lat = loc.lat();
        const lng = loc.lng();
        const parts = { address_line1: "", city: "", state: "", pincode: "" };
        const street = [];
        for (const c of place.address_components || []) {
          if (c.types.includes("premise") || c.types.includes("subpremise") || c.types.includes("street_number") || c.types.includes("route")) street.push(c.long_name);
          if (c.types.includes("sublocality_level_1") || c.types.includes("sublocality")) street.push(c.long_name);
          if (c.types.includes("locality")) parts.city = c.long_name;
          if (c.types.includes("administrative_area_level_1")) parts.state = c.long_name;
          if (c.types.includes("postal_code")) parts.pincode = c.long_name;
        }
        parts.address_line1 = street.join(", ");
        onAddressParts?.(parts);
        onPick({
          latitude: lat, longitude: lng,
          formatted_address: place.formatted_address || "",
          google_maps_url: `https://www.google.com/maps?q=${lat},${lng}`,
          location_source: "google_search",
        });
      });
      acRef.current = ac;
    } catch (e) {
      toast.error("Address search unavailable — check that Places API is enabled for this key.");
    }
  }, [places, onPick, onAddressParts]);
  return (
    <input
      ref={inputRef}
      placeholder="Start typing your address…"
      className="w-full px-3 py-2 rounded-md border border-[var(--hk-border)] bg-white/70 text-[14px]"
      data-testid={`${testid}-search-input`}
    />
  );
};

// ---------- Main picker ----------

export const DeliveryLocationPicker = ({ value, onChange, onAddressParts, config, testid = "maps", compact = false }) => {
  const [mode, setMode] = useState(null); // null | search | pin | link
  const [locating, setLocating] = useState(false);
  const [link, setLink] = useState("");
  const reverseGeocodeRef = useRef(null);

  if (!config?.enabled || !config?.api_key) return null;

  const clear = () => { onChange(null); setMode(null); setLink(""); };

  const useCurrentLocation = () => {
    if (!("geolocation" in navigator)) {
      toast.error("This browser does not support location access.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        let formatted = "";
        try {
          if (window.google?.maps?.Geocoder) {
            const g = new window.google.maps.Geocoder();
            const r = await g.geocode({ location: { lat, lng } });
            formatted = r?.results?.[0]?.formatted_address || "";
          }
        } catch { /* reverse geocode is best-effort */ }
        onChange({
          latitude: lat, longitude: lng,
          formatted_address: formatted,
          google_maps_url: `https://www.google.com/maps?q=${lat},${lng}`,
          location_source: "current_location",
        });
        setLocating(false);
        toast.success("Location captured — please confirm it on the map below.");
        setMode("pin"); // show the map so the customer can confirm/nudge the pin
      },
      () => {
        setLocating(false);
        toast.message("Location permission denied — no problem, continue with your written address.");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const applyLink = () => {
    const parsed = parseMapsLink(link);
    if (!parsed.valid) {
      toast.error("That doesn't look like a Google Maps link.");
      return;
    }
    onChange({
      latitude: parsed.lat, longitude: parsed.lng,
      formatted_address: "",
      google_maps_url: link.trim(),
      location_source: "pasted_link",
    });
    toast.success(parsed.lat != null ? "Location read from link." : "Maps link saved with your address.");
  };

  const optBtn = (active) =>
    "flex items-center gap-2 text-[12.5px] px-3 py-2 rounded-md border transition-colors " +
    (active ? "border-[var(--hk-orange)] bg-[var(--hk-warm-ivory)] text-[var(--hk-orange-deep)]" : "border-[var(--hk-border)] bg-white/70 text-[var(--hk-ink)] hover:border-[var(--hk-ink)]");

  const body = (
    <>
      <div className="flex flex-wrap gap-2">
        {config.current_location && (
          <button type="button" onClick={useCurrentLocation} disabled={locating} className={optBtn(false)} data-testid={`${testid}-current-location-btn`}>
            {locating ? <Loader2 size={13} className="animate-spin" /> : <Navigation size={13} />} Use My Current Location
          </button>
        )}
        {config.autocomplete && (
          <button type="button" onClick={() => setMode(mode === "search" ? null : "search")} className={optBtn(mode === "search")} data-testid={`${testid}-search-btn`}>
            <Search size={13} /> Search Address on Google Maps
          </button>
        )}
        {config.map_pin && (
          <button type="button" onClick={() => setMode(mode === "pin" ? null : "pin")} className={optBtn(mode === "pin")} data-testid={`${testid}-pin-btn`}>
            <MapPin size={13} /> Drop / Select Pin on Map
          </button>
        )}
        {config.paste_link && (
          <button type="button" onClick={() => setMode(mode === "link" ? null : "link")} className={optBtn(mode === "link")} data-testid={`${testid}-link-btn`}>
            <Link2 size={13} /> Paste Google Maps Link
          </button>
        )}
      </div>

      {mode === "search" && (
        <APIProvider apiKey={config.api_key}>
          <SearchBox onPick={(v) => { onChange(v); }} onAddressParts={onAddressParts} testid={testid} />
        </APIProvider>
      )}
      {mode === "pin" && (
        <APIProvider apiKey={config.api_key}>
          <PinMap value={value} onPick={onChange} testid={testid} />
        </APIProvider>
      )}
      {mode === "link" && (
        <div className="flex gap-2" data-testid={`${testid}-link-panel`}>
          <input
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="https://maps.app.goo.gl/… or https://www.google.com/maps/…"
            className="flex-1 px-3 py-2 rounded-md border border-[var(--hk-border)] bg-white/70 text-[13px]"
            data-testid={`${testid}-link-input`}
          />
          <button type="button" onClick={applyLink} className="hk-btn-ghost h-10 px-3 text-[12px]" data-testid={`${testid}-link-save-btn`}>Save link</button>
        </div>
      )}

      {value && (value.latitude != null || value.google_maps_url) && (
        <div className="flex items-start justify-between gap-3 p-3 rounded-md border border-emerald-200 bg-emerald-50/60" data-testid={`${testid}-saved-chip`}>
          <div className="text-[12px] text-[var(--hk-ink)] min-w-0">
            <p className="flex items-center gap-1.5 font-medium text-emerald-800"><CheckCircle2 size={13} /> Location saved ({SOURCE_LABELS[value.location_source] || "map"})</p>
            {value.formatted_address && <p className="mt-1 text-[var(--hk-ink-2)] truncate">{value.formatted_address}</p>}
            {value.latitude != null && <p className="mt-0.5 font-mono text-[11px] text-[var(--hk-ink-3)]">{value.latitude.toFixed(6)}, {value.longitude.toFixed(6)}</p>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {mapsUrlFor(value) && (
              <a href={mapsUrlFor(value)} target="_blank" rel="noreferrer" className="text-[11px] flex items-center gap-1 text-[var(--hk-orange-deep)] underline" data-testid={`${testid}-open-link`}>
                <ExternalLink size={11} /> Open
              </a>
            )}
            <button type="button" onClick={clear} className="text-[var(--hk-ink-3)] hover:text-red-600" data-testid={`${testid}-clear-btn`}><X size={14} /></button>
          </div>
        </div>
      )}
    </>
  );

  if (compact) return <div className="space-y-3" data-testid={`${testid}-picker`}>{body}</div>;
  return (
    <div className="hk-card p-6 space-y-4" data-testid={`${testid}-picker`}>
      <div>
        <h3 className="hk-h5 flex items-center gap-2"><MapPin size={15} className="text-[var(--hk-orange-deep)]" /> Add Google Maps Location <span className="text-[10px] font-normal uppercase tracking-widest text-[var(--hk-ink-3)]">optional</span></h3>
        <p className="text-[12.5px] text-[var(--hk-ink-2)] mt-1 leading-relaxed">
          Pinpoint your exact doorstep so our delivery partner finds you faster. This enhances your written address — it never replaces it.
        </p>
      </div>
      {body}
    </div>
  );
};

export default DeliveryLocationPicker;
